import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';

import { ConfigsService, LocaleService } from '@sm360/widget-commons-core-services';
import { AnalyticsService, AccountService } from '@sm360/widget-commons-general';
import { Quote } from '@sm360/widget-commons-vehicle';
import { Wizard } from '@sm360/widget-commons-forms';
import Logger from '@sm360/logger-sentry';

import CarStory from 'trade-in-360/components/OfficialTradeInForm/components/CarStory/CarStory.jsx';
import CarCondition from 'trade-in-360/components/OfficialTradeInForm/components/CarCondition/CarCondition.jsx';
import TradeInService from 'trade-in-360/services/TradeInService/TradeInService';
import TradeInModel from 'trade-in-360/model/TradeInModel';

import 'trade-in-360/components/OfficialTradeInForm/OfficialTradeInForm.scss';

const STEPS = {
    CAR_STORY: 0,
    CAR_CONDITION: 1,
};

class OfficialTradeInForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            completedSteps: {
                [STEPS.CAR_STORY]: false,
                [STEPS.CAR_CONDITION]: false,
            },
            stepInputValues: {
                [STEPS.CAR_STORY]: {},
                [STEPS.CAR_CONDITION]: {},
            },
            currentStep: STEPS.CAR_STORY,
            tradeInVehicle: {},
        };

        this.handleStepFormSubmit = this.handleStepFormSubmit.bind(this);
        this.allStepCompleted = this.allStepCompleted.bind(this);
        this.goNextStep = this.goNextStep.bind(this);
        this.saveApiTradeIn = this.saveApiTradeIn.bind(this);
        this.logger = new Logger(this.constructor.displayName, ConfigsService.getLoggerContext());
    }

    componentDidMount() {
        if (AccountService.isAuthenticated()) {
            const delay = () => new Promise((resolve) => setTimeout(() => resolve(true), 4000));
            delay().then(() =>
                TradeInService.fetchTradeIn(this.props.config).then((tradeIn) => {
                    this.setState({ tradeInVehicle: tradeIn });
                })
            );
        }
    }

    allStepCompleted() {
        return Object.values(this.state.completedSteps).every((step) => step);
    }

    goNextStep() {
        this.setState((oldState) => {
            return {
                currentStep: oldState.currentStep + 1,
                completedSteps: {
                    ...oldState.completedSteps,
                    [oldState.currentStep]: true,
                },
            };
        });
    }

    convertBooleansToText(obj) {
        return Object.keys(obj).reduce((accum, key) => {
            accum[key] = typeof obj[key] === 'boolean' ? `${obj[key]}` : obj[key];
            return accum;
        }, {});
    }

    saveApiTradeIn() {
        // check which completed steps
        const { tradeInVehicle } = this.state;
        const { evaulationQuestion: prevEvaluationQuestion = {} } = tradeInVehicle;
        let evaluationQuestion = {};

        if (this.state.currentStep === STEPS.CAR_STORY) {
            evaluationQuestion = {
                ...prevEvaluationQuestion,
                ...this.state.stepInputValues[STEPS.CAR_STORY],
            };
        } else {
            evaluationQuestion = {
                ...prevEvaluationQuestion,
                ...this.state.stepInputValues[STEPS.CAR_STORY],
                ...this.state.stepInputValues[STEPS.CAR_CONDITION],
            };
        }

        return new Promise((resolve, reject) => {
            const tradeIn = new TradeInModel({
                id: tradeInVehicle.id,
                leadId: null,
                profile: null,
                tradeInVehicle: null,
                estimation: null,
                evaluationQuestion,
            });

            return TradeInService.saveTradeIn(tradeIn, this.props.quote, false, this.props.config)
                .then((updatedTradeIn) => {
                    this.props.loadQuote();
                    resolve(updatedTradeIn);
                })
                .catch((e) => {
                    this.logger.error('An error happened while saving the showroom tradeInVehicle', e, tradeIn);
                    reject(e);
                });
        });
    }

    handleStepFormSubmit(inputValues) {
        function saveTradeIn() {
            this.saveApiTradeIn(inputValues).then(() => {
                if (this.state.completedSteps[STEPS.CAR_STORY] && !this.state.completedSteps[STEPS.CAR_CONDITION]) {
                    AnalyticsService.eventDataLayer('form|trade-in - Car Story', 'submit', 'trade-in-showroom');
                } else if (this.state.completedSteps[STEPS.CAR_CONDITION]) {
                    AnalyticsService.eventDataLayer('form|trade-in - Car Condition', 'submit', 'trade-in-showroom');
                }
                this.goNextStep();
            });
        }

        this.setState(
            {
                stepInputValues: {
                    ...this.state.stepInputValues,
                    [this.state.currentStep]: inputValues,
                },
            },
            saveTradeIn
        );
    }

    render() {
        const onStepTransition = (inputValues, goNextStep) => {
            this.handleStepFormSubmit(inputValues);
            goNextStep();
        };
        const defaultInputs = this.state.tradeInVehicle.evaluationQuestion || {};

        const steps = [
            ({ goNextStep }) => (
                <CarStory
                    defaultInputs={defaultInputs}
                    onFormSubmit={(inputValues) => onStepTransition(inputValues, goNextStep)}
                />
            ),
            () => (
                <CarCondition
                    onFormSubmit={(inputValues) => onStepTransition(inputValues, this.props.onFormCompletion)}
                />
            ),
        ];
        return (
            <div className="officialTradeInForm">
                <h3 className="sr-subtitle-1">{LocaleService.t('TRADE_IN.OFFICIAL_TITLE')}</h3>
                <Wizard steps={steps} />
            </div>
        );
    }
}

OfficialTradeInForm.propTypes = {
    quote: PropTypes.instanceOf(Quote.QuoteModel),
    tradeInValues: PropTypes.shape({
        amount: PropTypes.number.isRequired,
        high: PropTypes.number.isRequired,
        low: PropTypes.number.isRequired,
    }).isRequired,
    onFormCompletion: PropTypes.func.isRequired,

    loadQuote: PropTypes.func,
    config: PropTypes.object,
};

OfficialTradeInForm.defaultProps = {
    quote: null,
    loadQuote: noop,
    config: {},
};

OfficialTradeInForm.displayName = 'OfficialTradeInForm';

export { STEPS, OfficialTradeInForm }; // For the tests

export default OfficialTradeInForm;
